import { ArrowLeft, CheckCircle, Eye, EyeOff, Plus, RefreshCw, Trash } from "lucide-react";
import React, { useCallback, useState } from "react";
import { graphql, useMutation } from "react-relay";
import { useNavigate } from "react-router";
import { RequireScope } from "src/components/RequireScope";
import { Button } from "src/components/ui/button";
import { Input } from "src/components/ui/input";
import { Label } from "src/components/ui/label";
import { SecretInput } from "src/components/ui/secret-input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/components/ui/select";
import { useToast } from "src/hooks/use-toast";
import { useSettingsMobileHeader } from "src/pages/SettingsPage/hooks/useSettingsMobileHeader";
import { CreateMcpServerRouteCreateMutation } from "src/pages/SettingsPage/routes/__generated__/CreateMcpServerRouteCreateMutation.graphql";
import { CreateMcpServerRouteTestUrlMutation } from "src/pages/SettingsPage/routes/__generated__/CreateMcpServerRouteTestUrlMutation.graphql";
import { buildMcpCredentials, createHeaderEntry, HeaderEntryType, MCP_SERVERS_PATH, redirectToOAuth } from "src/pages/SettingsPage/routes/mcp-server-utils";
import McpOauthAdvancedSettings from "src/pages/SettingsPage/routes/McpOauthAdvancedSettings";

const createServerMutation = graphql`
  mutation CreateMcpServerRouteCreateMutation($input: CreateCustomMcpServerInput!) {
    createCustomMcpServer(input: $input) {
      server {
        id
        name
        description
        url
        authType
        isActive
        discoveredTools
        toolCount
      }
      error
      oauthUrl
    }
  }
`;

const testUrlMutation = graphql`
  mutation CreateMcpServerRouteTestUrlMutation($input: TestCustomMcpServerUrlInput!) {
    testCustomMcpServerUrl(input: $input) {
      success
      tools
      error
    }
  }
`;

export default function CreateMcpServerRoute() {
  return (
    <RequireScope scope="write:integrations">
      <CreateMcpServerRouteContent />
    </RequireScope>
  );
}

function CreateMcpServerRouteContent() {
  useSettingsMobileHeader("Add MCP Server");
  const navigate = useNavigate();
  const { toast } = useToast();

  const [commitCreate, isCreating] = useMutation<CreateMcpServerRouteCreateMutation>(createServerMutation);
  const [commitTestUrl] = useMutation<CreateMcpServerRouteTestUrlMutation>(testUrlMutation);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [url, setUrl] = useState("");
  const [authType, setAuthType] = useState("API_KEY");
  const [apiKey, setApiKey] = useState("");
  const [oauthClientId, setOauthClientId] = useState("");
  const [headers, setHeaders] = useState<HeaderEntryType[]>([createHeaderEntry()]);
  const [connectionTest, setConnectionTest] = useState<{
    status: "untested" | "testing" | "success" | "failed";
    tools: readonly string[];
    error: string | null;
  }>({ status: "untested", tools: [], error: null });

  const [showSecrets, setShowSecrets] = useState(false);
  const [oauthMethodOverride, setOauthMethodOverride] = useState("auto");
  const isTesting = connectionTest.status === "testing";
  const isOAuth = authType === "OAUTH";

  const hasIncompleteHeaders =
    authType === "CUSTOM_HEADERS" &&
    headers.some((h) => (h.key.trim() && !h.value.trim()) || (!h.key.trim() && h.value.trim()));
  const hasNoValidHeaders =
    authType === "CUSTOM_HEADERS" && !headers.some((h) => h.key.trim() && h.value.trim());

  const resetTest = () => setConnectionTest({ status: "untested", tools: [], error: null });

  const canTestUrl =
    !!url && !isTesting && !hasIncompleteHeaders && !hasNoValidHeaders;
  const canSave = isOAuth
    ? !!name.trim() && !!description.trim() && !!url
    : !!name.trim() && !!description.trim() && !!url && connectionTest.status === "success";

  const handleTestUrl = useCallback(() => {
    setConnectionTest({ status: "testing", tools: [], error: null });
    const credentials = buildMcpCredentials(authType, apiKey, headers);
    commitTestUrl({
      variables: {
        input: {
          url,
          authType,
          credentials: credentials ?? null,
        },
      },
      onCompleted: (response) => {
        const result = response.testCustomMcpServerUrl;
        if (result?.success) {
          setConnectionTest({ status: "success", tools: result.tools ?? [], error: null });
        } else {
          setConnectionTest({ status: "failed", tools: [], error: result?.error ?? "Connection failed" });
        }
      },
      onError: (error) => {
        setConnectionTest({ status: "failed", tools: [], error: error.message });
      },
    });
  }, [url, authType, apiKey, headers, commitTestUrl]);

  const handleCreate = useCallback(() => {
    let credentials = buildMcpCredentials(authType, apiKey, headers, oauthClientId);
    // For OAuth servers with a method override, include it in credentials
    if (isOAuth && oauthMethodOverride && oauthMethodOverride !== "auto") {
      const overrideCreds = credentials ? JSON.parse(credentials) : {};
      overrideCreds.token_auth_method_override = oauthMethodOverride;
      credentials = JSON.stringify(overrideCreds);
    }
    commitCreate({
      variables: {
        input: {
          name: name.trim(),
          url,
          authType,
          credentials: credentials ?? null,
          description: description.trim() || null,
        },
      },
      onCompleted: (response) => {
        const result = response.createCustomMcpServer;
        if (result?.oauthUrl) {
          redirectToOAuth(result.oauthUrl, toast);
          return;
        }
        if (result?.error && !result.server) {
          toast({ title: "Error", description: result.error, variant: "error" });
        } else {
          if (result?.error) {
            toast({ title: "Server added", description: result.error, variant: "error" });
          } else {
            toast({ title: "MCP Server added", description: `${name} has been configured.` });
          }
          navigate(MCP_SERVERS_PATH);
        }
      },
      onError: (error) => {
        toast({ title: "Error", description: error.message, variant: "error" });
      },
    });
  }, [name, description, url, authType, apiKey, headers, oauthClientId, isOAuth, oauthMethodOverride, commitCreate, toast, navigate]);

  return (
    <div className="flex-1 px-4 md:px-5 pt-4 pb-5">
      {/* Header with back button */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => navigate(MCP_SERVERS_PATH)}
          className="p-2 hover:bg-accent rounded-lg transition-colors"
        >
          <ArrowLeft className="h-5 w-5 text-muted-foreground" />
        </button>
        <h1 className="text-xl font-semibold text-foreground">Add MCP Server</h1>
      </div>

      <div className="max-w-4xl space-y-6">
        {/* Basic Info Section */}
        <div className="bg-card/30 rounded-lg p-6 space-y-5">
          <div className="space-y-2">
            <Label htmlFor="mcp-server-name">Server Name <span className="text-red-500">*</span></Label>
            <Input
              id="mcp-server-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. My MCP Server"
              className="max-w-md"
              disabled={isTesting}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="mcp-server-description">Description <span className="text-red-500">*</span></Label>
            <p className="text-muted-foreground text-sm">
              Describe what this MCP server handles. This is used to decide when to use tools from this MCP server.
            </p>
            <textarea
              id="mcp-server-description"
              className="w-full max-w-2xl rounded-md border border-input bg-background px-3 py-2 text-sm resize-none"
              placeholder="e.g. Query and analyze PostgreSQL databases"
              rows={4}
              maxLength={500}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label>MCP Server Instructions</Label>
            <p className="text-muted-foreground text-sm">
              After adding the MCP server, you will be able to modify MCP server specific context and instructions.
            </p>
          </div>
        </div>

        {/* Connection Settings Section */}
        <div className="bg-card/30 rounded-lg p-6 space-y-5">
          <div>
            <Label>Connection Settings</Label>
            <p className="text-muted-foreground text-sm mt-1">
              Server URL and authentication configuration.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="mcp-server-url">Server URL</Label>
            <Input
              id="mcp-server-url"
              type="url"
              autoComplete="off"
              value={url}
              className="max-w-md"
              disabled={isTesting}
              onChange={(e) => {
                setUrl(e.target.value);
                resetTest();
              }}
              placeholder="https://mcp.example.com"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="mcp-server-auth-type">Auth Type</Label>
            <Select
              value={authType}
              disabled={isTesting}
              onValueChange={(v) => {
                setAuthType(v);
                setApiKey("");
                setHeaders([createHeaderEntry()]);
                resetTest();
              }}
            >
              <SelectTrigger id="mcp-server-auth-type" className="max-w-md">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="API_KEY">Bearer Token</SelectItem>
                <SelectItem value="CUSTOM_HEADERS">Custom Headers</SelectItem>
                <SelectItem value="OAUTH">OAuth</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {authType === "API_KEY" && (
            <div className="space-y-2">
              <Label htmlFor="mcp-server-api-key">Bearer Token</Label>
              <div className="max-w-md">
                <SecretInput
                  id="mcp-server-api-key"
                  placeholder="Enter bearer token"
                  value={apiKey}
                  disabled={isTesting}
                  onChange={(e) => {
                    setApiKey(e.target.value);
                    resetTest();
                  }}
                />
              </div>
            </div>
          )}

          {authType === "CUSTOM_HEADERS" && (
            <div className="space-y-2" role="group" aria-labelledby="mcp-server-headers-label">
              <Label id="mcp-server-headers-label">Headers</Label>
              {headers.map((header, idx) => (
                <div key={header.id} className="flex gap-2 max-w-lg">
                  <input
                    type="text"
                    autoComplete="off"
                    className="w-2/5 rounded-md border border-input bg-background px-3 py-2 text-sm disabled:opacity-50"
                    aria-label={`Header ${idx + 1} name`}
                    placeholder="Header name"
                    value={header.key}
                    disabled={isTesting}
                    onChange={(e) => {
                      setHeaders((h) => h.map((entry, i) => (i === idx ? { ...entry, key: e.target.value } : entry)));
                      resetTest();
                    }}
                  />
                  <div className="relative flex-1">
                    <input
                      type={showSecrets ? "text" : "password"}
                      autoComplete="off"
                      className="w-full rounded-md border border-input bg-background pl-3 pr-8 py-2 text-sm disabled:opacity-50"
                      aria-label={`Header ${idx + 1} value`}
                      placeholder="Value"
                      value={header.value}
                      disabled={isTesting}
                      onChange={(e) => {
                        setHeaders((h) => h.map((entry, i) => (i === idx ? { ...entry, value: e.target.value } : entry)));
                        resetTest();
                      }}
                    />
                    <button
                      type="button"
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground transition-colors"
                      onClick={() => setShowSecrets((s) => !s)}
                      tabIndex={-1}
                    >
                      {showSecrets ? <EyeOff className="size-3.5" /> : <Eye className="size-3.5" />}
                    </button>
                  </div>
                  {headers.length > 1 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="px-2"
                      disabled={isTesting}
                      onClick={() => {
                        setHeaders((h) => h.filter((_, i) => i !== idx));
                        resetTest();
                      }}
                    >
                      <Trash className="size-3.5" />
                    </Button>
                  )}
                </div>
              ))}
              <Button
                variant="outline"
                size="sm"
                disabled={isTesting}
                onClick={() => setHeaders((h) => [...h, createHeaderEntry()])}
              >
                <Plus className="size-3.5 mr-1" />
                Add Header
              </Button>
              {hasIncompleteHeaders && (
                <p className="text-xs text-destructive">Each header must have both a name and a value.</p>
              )}
              {!hasIncompleteHeaders && hasNoValidHeaders && (
                <p className="text-xs text-destructive">At least one header is required.</p>
              )}
            </div>
          )}

          {isOAuth && (
            <div className="space-y-2">
              <Label htmlFor="mcp-oauth-client-id">Client ID <span className="text-muted-foreground">(optional)</span></Label>
              <Input
                id="mcp-oauth-client-id"
                autoComplete="off"
                value={oauthClientId}
                className="max-w-md"
                disabled={isTesting}
                onChange={(e) => setOauthClientId(e.target.value)}
                placeholder="Leave blank to auto-register"
              />
            </div>
          )}

          {isOAuth && (
            <p className="text-xs text-muted-foreground">
              OAuth authorization will begin after adding the server. You'll be redirected to the provider to
              complete setup.
            </p>
          )}

          {isOAuth && (
            <McpOauthAdvancedSettings
              url={url}
              selectedMethod={oauthMethodOverride}
              onMethodChange={setOauthMethodOverride}
            />
          )}

        </div>

        {!isOAuth && connectionTest.status !== "success" && (
          <p className="text-xs text-yellow-500">
            Test Connection is required before adding a server.
          </p>
        )}

        {/* Connection test result */}
        {connectionTest.status === "success" && (
          <div className="flex items-center gap-2 rounded-md border border-green-500/30 bg-green-500/10 px-3 py-2 max-w-lg">
            <CheckCircle className="size-4 text-green-500 flex-shrink-0" />
            <span className="text-sm text-green-500">
              Connected — {connectionTest.tools.length} tool{connectionTest.tools.length !== 1 ? "s" : ""}{" "}
              discovered
            </span>
          </div>
        )}
        {connectionTest.status === "failed" && (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 max-w-lg">
            <p className="text-sm text-destructive">{connectionTest.error}</p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center justify-end gap-3 pt-4">
          <Button
            variant="outline"
            onClick={() => navigate(MCP_SERVERS_PATH)}
            disabled={isCreating}
          >
            Cancel
          </Button>
          {!isOAuth && (
            <Button
              variant="outline"
              onClick={handleTestUrl}
              disabled={!canTestUrl || connectionTest.status === "testing"}
            >
              <RefreshCw
                className={`size-3.5 mr-1.5 ${connectionTest.status === "testing" ? "animate-spin" : ""}`}
              />
              {connectionTest.status === "testing" ? "Testing..." : "Test Connection"}
            </Button>
          )}
          <Button
            onClick={handleCreate}
            disabled={isCreating || !canSave}
            loading={isCreating}
            loadingText="Adding..."
          >
            Add Server
          </Button>
        </div>
      </div>
    </div>
  );
}
